import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const LINKS = [
  { label: "About", id: "about" },
  { label: "Classes", id: "classes" },
  { label: "Coaches", id: "coaches" },
  { label: "Schedule", id: "schedule" },
  { label: "Gallery", id: "gallery" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    // Close mobile drawer first so the scroll target isn't hidden behind it.
    setOpen(false);

    const doScroll = () => {
      const target = document.getElementById(id);
      if (!target) return;
      // Sticky header is h-20 (80px). Add a small breathing gap so the
      // heading is not glued to the header edge.
      const HEADER_OFFSET = -96;
      // Prefer Lenis (owns the scroll on desktop + wheel/touch on mobile).
      const lenis = window.__lenis;
      if (lenis && typeof lenis.scrollTo === "function") {
        lenis.scrollTo(target, { offset: HEADER_OFFSET, duration: 1.0 });
        return;
      }
      // Fallback: native smooth scroll with manual offset.
      const rect = target.getBoundingClientRect();
      const top = rect.top + window.pageYOffset + HEADER_OFFSET;
      window.scrollTo({ top, behavior: "smooth" });
    };

    if (location.pathname !== "/") {
      navigate("/");
      // Wait for the home route to mount, then scroll.
      setTimeout(doScroll, 180);
    } else {
      // Defer one frame so the drawer close paints before scroll starts.
      requestAnimationFrame(doScroll);
    }
  };

  const goBook = () => {
    setOpen(false);
    navigate("/book");
    window.scrollTo({ top: 0 });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "bg-black/60 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto max-w-[1400px] px-6 lg:px-10 h-20 flex items-center justify-between">
        <button
          onClick={() => go("hero")}
          data-testid="nav-logo"
          className="font-display uppercase tracking-tight text-lg md:text-xl font-700 leading-none text-left"
        >
          <span className="text-white">WAR ROOM</span>
          <span className="text-hotpink"> /</span>
          <span className="block text-[10px] tracking-[0.35em] text-neutral-400 font-body">
            PERFORMANCE
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-${l.id}`}
              className="group relative font-body text-sm uppercase tracking-[0.18em] text-neutral-300 hover:text-white transition-colors duration-300"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-hotpink transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
          <button
            onClick={goBook}
            data-testid="nav-book-btn"
            className="font-display uppercase tracking-wide text-sm px-6 py-2.5 bg-hotpink text-white hover:bg-white hover:text-ink transition-colors duration-300 rounded-sm whitespace-nowrap"
          >
            Book Free Class
          </button>
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden bg-black/90 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-2">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  data-testid={`nav-mobile-${l.id}`}
                  className="text-left font-display uppercase tracking-wide text-xl text-neutral-200 min-h-[48px] py-3 border-b border-white/5 last:border-b-0 active:text-hotpink"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={goBook}
                data-testid="nav-mobile-book-btn"
                className="mt-3 font-display uppercase tracking-wide text-base min-h-[48px] px-6 py-3 bg-hotpink text-white rounded-sm"
              >
                Book Free Class
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
